/**
 * Help Center search: instant, client-side, no dependencies.
 *
 * Every query word must match somewhere in an article (title, keywords,
 * headings, summary or body); words are stemmed and matched by prefix so
 * results appear while typing. Titles weigh most, the body least, and a few
 * synonyms catch the words customers actually use ("luggage", "PNR").
 * If no article matches every word, the best partial matches are shown.
 */

const STOPWORDS = new Set([
  'a', 'an', 'the', 'to', 'of', 'for', 'and', 'or', 'is', 'are', 'was', 'were', 'my', 'i', 'me',
  'do', 'does', 'did', 'can', 'could', 'how', 'what', 'when', 'where', 'why', 'which', 'in', 'on',
  'with', 'it', 'be', 'will', 'if', 'you', 'your', 'from', 'at', 'as', 'by', 'this', 'that', 'im',
  'there', 'about', 'should', 'would', 'please', 'have', 'has', 'need', 'want',
]);

/** Customer words → words the articles use. */
const SYNONYMS = {
  luggage: ['bag', 'baggage'],
  suitcase: ['bag', 'baggage'],
  pnr: ['confirmation'],
  locator: ['confirmation'],
  // Customers say "ticket"; the Help Center says "confirmation number".
  ticket: ['confirmation'],
  tickets: ['confirmation'],
  eticket: ['confirmation'],
  egift: ['gift'],
  itinerary: ['confirmation', 'statement'],
  legit: ['safe', 'legitimate'],
  scam: ['safe', 'legitimate'],
  money: ['refund', 'credit'],
  payoff: ['pay', 'early'],
  login: ['sign'],
  signin: ['sign'],
  phone: ['call', 'contact'],
  agent: ['contact', 'person'],
  human: ['contact', 'person'],
  hotel: ['stay', 'hotel'],
  insurance: ['insurance', 'faye'],
  reschedule: ['change'],
  points: ['miles'],
};

const FIELD_WEIGHTS = { title: 8, keywords: 6, headings: 4, summary: 3, body: 1 };

export function normalize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9¢$]+/g, ' ')
    .trim();
}

/** Crude English stem: enough to make "cancelling"/"cancelled"/"cancel" meet. */
export function stem(word) {
  let w = word;
  if (w.length > 5 && w.endsWith('ing')) w = w.slice(0, -3);
  else if (w.length > 4 && w.endsWith('ed')) w = w.slice(0, -2);
  else if (w.length > 4 && w.endsWith('es')) w = w.slice(0, -2);
  else if (w.length > 3 && w.endsWith('s')) w = w.slice(0, -1);
  if (w.length > 4 && w.endsWith(w[w.length - 1].repeat(2))) w = w.slice(0, -1);
  return w;
}

function words(text) {
  return normalize(text).split(' ').filter(Boolean);
}

export function queryTerms(query) {
  // "e-ticket" / "e-gift": the lone letter would prefix-match everything.
  const joined = normalize(query).replace(/\be (?=[a-z])/g, 'e');
  return joined.split(' ').filter((w) => w && !STOPWORDS.has(w)).map(stem).filter(Boolean);
}

/** Pre-compute each article's stemmed word sets per field. */
export function createSearchIndex(articles) {
  return (articles || []).map((article) => {
    const fields = {
      title: article.title,
      keywords: (article.keywords || []).join(' '),
      headings: (article.headings || []).map((h) => h.text).join(' '),
      summary: article.summary,
      body: article.plainText || '',
    };
    const stems = {};
    for (const [name, text] of Object.entries(fields)) {
      stems[name] = [...new Set(words(text).map(stem))];
    }
    return { article, stems, titleNorm: normalize(article.title) };
  });
}

function fieldScore(stemsInField, term, isLastTerm) {
  for (const s of stemsInField) {
    if (s === term) return 1;
    // The word being typed matches as a prefix; finished words need 4+ letters to prefix-match.
    if ((isLastTerm || term.length >= 4) && s.startsWith(term)) return 0.7;
  }
  return 0;
}

function termScore(entry, term, isLastTerm) {
  let best = 0;
  for (const [field, weight] of Object.entries(FIELD_WEIGHTS)) {
    const hit = fieldScore(entry.stems[field], term, isLastTerm);
    if (hit) best = Math.max(best, hit * weight);
  }
  return best;
}

function snippetFor(article, terms) {
  const text = article.plainText || article.summary || '';
  const sentences = text.split(/(?<=[.!?])\s+|\n+/).filter((s) => s.trim().length > 20);
  const hit = sentences.find((s) => {
    const st = words(s).map(stem);
    return terms.some((t) => st.some((w) => w.startsWith(t)));
  });
  const chosen = (hit || article.summary || sentences[0] || '').trim();
  return chosen.length > 180 ? `${chosen.slice(0, 177).trimEnd()}…` : chosen;
}

/**
 * Search the index. Returns `[{ article, score, snippet }]`, best first.
 */
export function searchHelp(index, query, { limit = 20 } = {}) {
  const base = queryTerms(query);
  if (!base.length) return [];
  const groups = base.map((t, i) => {
    const alts = (SYNONYMS[t] || []).map(stem);
    return { terms: [t, ...alts], isLast: i === base.length - 1 };
  });
  const phrase = normalize(query);

  const scored = index.map((entry) => {
    let total = 0;
    let matched = 0;
    for (const g of groups) {
      const s = Math.max(...g.terms.map((t) => termScore(entry, t, g.isLast)));
      if (s > 0) { matched += 1; total += s; }
    }
    if (phrase.length > 3 && entry.titleNorm.includes(phrase)) total += 10;
    return { entry, total, matched };
  }).filter((r) => r.matched > 0);

  const all = scored.filter((r) => r.matched === groups.length);
  const pool = all.length ? all : scored.filter((r) => r.matched >= Math.max(1, groups.length - 1));
  const terms = groups.flatMap((g) => g.terms);
  return pool
    .sort((a, b) => b.total - a.total || a.entry.article.title.localeCompare(b.entry.article.title))
    .slice(0, limit)
    .map((r) => ({ article: r.entry.article, score: r.total, snippet: snippetFor(r.entry.article, terms) }));
}
